// Full implementation of a Staff Report View using MUI and mock data

import React from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  Button,
  Stack,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const statData = [
  { title: "Total Staff", value: 120 },
  { title: "Active Staff", value: 95 },
  { title: "On Leave", value: 15 },
  { title: "Inactive Staff", value: 10 },
];

const staffStats = [
  {
    name: "Rakesh Menon",
    department: "Client Relations",
    attendance: 93,
    onLeave: false,
  },
  {
    name: "Deepa Rajan",
    department: "Candidate Sourcing",
    attendance: 87,
    onLeave: true,
  },
  {
    name: "Sajith Kumar",
    department: "Operations",
    attendance: 91,
    onLeave: false,
  },
];


const attendanceOverTime = [
  { month: "Jan", attendance: 85 },
  { month: "Feb", attendance: 88 },
  { month: "Mar", attendance: 90 },
  { month: "Apr", attendance: 89 },
  { month: "May", attendance: 91 },
  { month: "Jun", attendance: 92 },
  { month: "Jul", attendance: 93 },
  { month: "Aug", attendance: 90 },
  { month: "Sep", attendance: 88 },
  { month: "Oct", attendance: 87 },
  { month: "Nov", attendance: 86 },
  { month: "Dec", attendance: 85 },
];

const StatCard = ({ title, value }) => (
  <Card
    sx={{
      minHeight: 100,
      textAlign: "center",
      boxShadow: "none",
      border: "1px solid lightgrey",
      borderRadius: "20px",
    }}
  >
    <CardContent>
      <Typography
         variant="h5"
         sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}

      >
        {title}
      </Typography>
      <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}

      >
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const StaffPerformanceTable = ({ data }) => (
  <TableContainer
    sx={{
      border: "1px solid transparent",
      borderRadius: "10px",
      maxWidth: "90vw",
      overflowX: "auto",
      margin: "auto",
    }}
  >
    <Table>
      <TableHead>
        <TableRow>
          <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
          <StyledTableCell>Staff Name</StyledTableCell>
          <StyledTableCell>Department</StyledTableCell>
          <StyledTableCell>Attendance %</StyledTableCell>
          <StyledTableCell>On Leave</StyledTableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((staff, idx) => (
          <StyledTableRow key={idx}>
            <StyledTableCell>{idx + 1}</StyledTableCell>
            <StyledTableCell>{staff.name}</StyledTableCell>
            <StyledTableCell>{staff.department}</StyledTableCell>
            <StyledTableCell>{staff.attendance}%</StyledTableCell>
            <StyledTableCell>{staff.onLeave ? "Yes" : "No"}</StyledTableCell>
          </StyledTableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const AttendanceChart = ({ data }) => (
  <Paper sx={{ mt: 4, p: 2 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Typography variant="h6">Attendance Over Time</Typography>
    
    </Stack>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <Tooltip />
        <Bar dataKey="attendance" fill="#006CB8" />
      </BarChart>
    </ResponsiveContainer>
  </Paper>
);

const StaffReportView = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={1}
      >
        Staff Reports
      </Typography>

      <Grid container spacing={2}>
        {statData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <StatCard title={item.title} value={item.value} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          mb={1}
        >
          Staff Performance
        </Typography>
        <StaffPerformanceTable data={staffStats} />
      </Box>

      <AttendanceChart data={attendanceOverTime} />
    </Box>
  );
};

export default StaffReportView;
